import { Delete, Download, Visibility } from "@mui/icons-material";
import * as MUI from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import FileIcon from "../../../../components/display/FileIcon";
import { isFileViewable } from "../../../../components/display/fileViewer/FileViewerUtils";
import { getFileExtension } from "../../../../components/form/fileUpload/utils/FileUploadFunctions";

const headerCells = [
  { title: "Name", align: "left" },
  { title: "Extension", align: "left" },
  { title: "Actions", align: "right" },
];

export default function AppointmentFilesTable({
  files,
  canDelete,
  loadingFileId,
  onView,
  onDownload,
  onDelete,
}) {
  const { t } = useTranslation();

  return (
    <MUI.Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: "divider",
        overflow: "hidden",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <MUI.TableContainer sx={{ overflowX: "auto", minWidth: 0 }}>
        <MUI.Table data-testid="appointment.details.files.table">
          <MUI.TableHead>
            <MUI.TableRow
              sx={{
                "& th": {
                  bgcolor: (theme) =>
                    theme.palette.mode === "light"
                      ? "grey.50"
                      : "rgba(255,255,255,0.04)",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: "text.secondary",
                  borderBottom: "2px solid",
                  borderBottomColor: "divider",
                },
              }}
            >
              {headerCells.map((cell) => (
                <MUI.TableCell key={cell.title} align={cell.align}>
                  {t(cell.title)}
                </MUI.TableCell>
              ))}
            </MUI.TableRow>
          </MUI.TableHead>
          <MUI.TableBody>
            {files.map((file) => (
              <AppointmentFileRow
                key={file?.fileId}
                file={file}
                canDelete={canDelete}
                isLoading={loadingFileId === file?.fileId}
                onView={() => onView(file)}
                onDownload={() => onDownload(file)}
                onDelete={() => onDelete(file)}
              />
            ))}
          </MUI.TableBody>
        </MUI.Table>
      </MUI.TableContainer>
    </MUI.Paper>
  );
}

function AppointmentFileRow({
  file,
  canDelete,
  isLoading,
  onView,
  onDownload,
  onDelete,
}) {
  const { t } = useTranslation();
  const extension = getFileExtension(file?.name);
  const isViewable = isFileViewable(file?.name);

  return (
    <MUI.TableRow hover>
      <MUI.TableCell sx={{ maxWidth: 0, width: "60%" }}>
        <MUI.Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
          <MUI.Box sx={{ display: "flex", flexShrink: 0, "& svg": { width: 24, height: 24 } }}>
            <FileIcon fileExtension={extension} />
          </MUI.Box>
          <MUI.Typography variant="body2" fontWeight={600} noWrap title={file?.name}>
            {file?.name}
          </MUI.Typography>
        </MUI.Stack>
      </MUI.TableCell>
      <MUI.TableCell>{extension ? extension.toUpperCase() : "-"}</MUI.TableCell>
      <MUI.TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
        {isLoading ? (
          <MUI.CircularProgress size={24} />
        ) : (
          <MUI.Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <MUI.Tooltip title={t(isViewable ? "View" : "PreviewNotAvailable")}>
              <span>
                <MUI.IconButton
                  size="small"
                  disabled={!isViewable}
                  onClick={onView}
                  data-testid="appointment.details.files.table.view-button"
                >
                  <Visibility fontSize="small" />
                </MUI.IconButton>
              </span>
            </MUI.Tooltip>
            <MUI.Tooltip title={t("Download")}>
              <MUI.IconButton
                size="small"
                onClick={onDownload}
                data-testid="appointment.details.files.table.download-button"
              >
                <Download fontSize="small" />
              </MUI.IconButton>
            </MUI.Tooltip>
            {canDelete ? (
              <MUI.Tooltip title={t("Delete")}>
                <MUI.IconButton
                  size="small"
                  color="error"
                  onClick={onDelete}
                  data-testid="appointment.details.files.table.delete-button"
                >
                  <Delete fontSize="small" />
                </MUI.IconButton>
              </MUI.Tooltip>
            ) : null}
          </MUI.Stack>
        )}
      </MUI.TableCell>
    </MUI.TableRow>
  );
}
